import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { listVideoJobs } from "../../videoStudio/api/listVideoJobs";
import { jobCategoryLabel, jobModelLabel, truncatePrompt } from "../../videoStudio/utils/jobLabels";
import { mediaSrc } from "../../../utils/mediaSrc";

const LIBRARY_FETCH_LIMIT = 100;

function VideoPickTile({ job, selected, onSelect, lang }) {
  const url = job.video_url ? mediaSrc(job.video_url) : "";
  const label =
    truncatePrompt(job.prompt, 40) ||
    jobCategoryLabel(job.category_id, lang) ||
    `#${job.id}`;

  return (
    <Box
      onClick={() => onSelect(job)}
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        cursor: "pointer",
        bgcolor: "#000",
        transition: "border-color 0.15s",
        "&:hover": { borderColor: selected ? "primary.main" : "primary.light" },
      }}
    >
      <Box sx={{ pt: "56.25%", position: "relative" }}>
        {url ? (
          <Box
            component="video"
            src={url}
            muted
            playsInline
            preload="metadata"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              p: 1,
              textAlign: "center",
            }}
          >
            {lang?.socialLibraryNoPreview || "No preview"}
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 1, bgcolor: "background.paper" }}>
        <Typography variant="caption" fontWeight={800} display="block" noWrap>
          {jobModelLabel(job.ui_model, lang)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
          {label}
        </Typography>
      </Box>
      {selected ? (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: alpha("#fff", 0.95),
            color: "primary.main",
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          {lang?.socialLibrarySelected || "Selected"}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   lang?: object;
 *   selectedJob?: object | null;
 *   onConfirm: (job: object) => void;
 * }} props
 */
export default function PickVideoLibraryModal({
  open,
  onClose,
  lang,
  selectedJob,
  onConfirm,
}) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [jobs, setJobs] = React.useState([]);
  const [pick, setPick] = React.useState(null);

  const loadPage = React.useCallback(async () => {
    setLoading(true);
    try {
      const [{ jobs: studioRows }, agentRes] = await Promise.all([
        listVideoJobs(hitAxios, { page: 1, limit: LIBRARY_FETCH_LIMIT, status: "completed" }),
        hitAxios({
          path: "/api/heygen/video-agent/jobs",
          post: false,
          showLoading: false,
          showSnackbar: false,
        }),
      ]);

      const studio = (studioRows || [])
        .filter((j) => j.status === "completed" && j.video_url)
        .map((j) => ({ ...j, _source: "studio" }));

      let agent = [];
      if (agentRes?.data?.success) {
        agent = (agentRes.data.data?.jobs || [])
          .filter((j) => j.status === "completed" && j.generated_video)
          .map((j) => ({
            id: `agent-${j.id}`,
            video_url: j.generated_video,
            prompt: j.prompt || j.title,
            ui_model: "video-agent",
            category_id: "ugc-spokesperson",
            status: "completed",
            _source: "agent",
          }));
      }

      setJobs([...studio, ...agent]);
    } catch (err) {
      showSnack?.(err?.message || lang?.socialLibraryLoadFailed || "Could not load videos.", false);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [hitAxios, lang, showSnack]);

  React.useEffect(() => {
    if (!open) return;
    setPick(selectedJob || null);
  }, [open, selectedJob]);

  React.useEffect(() => {
    if (!open) return;
    loadPage();
  }, [open, loadPage]);

  const handleConfirm = () => {
    if (!pick?.video_url) return;
    onConfirm({
      ...pick,
      video_url: mediaSrc(pick.video_url),
    });
    onClose();
  };

  const typeHint =
    lang?.socialLibraryHintVideo || "Select any completed video from your library.";
  const modalTitle = lang?.socialPickLibraryTitle || "Choose video from library";
  const emptyMsg =
    lang?.socialLibraryEmpty ||
    "No videos yet. Generate or upload clips in Video Studio, then return here.";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
        <VideoLibraryOutlined color="primary" />
        {modalTitle}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {typeHint}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length === 0 ? (
            <Alert severity="info">{emptyMsg}</Alert>
          ) : (
            <Grid container spacing={1.5}>
              {jobs.map((job) => (
                <Grid item xs={6} sm={4} md={3} key={job.id}>
                  <VideoPickTile
                    job={job}
                    selected={pick?.id === job.id}
                    onSelect={setPick}
                    lang={lang}
                  />
                </Grid>
              ))}
            </Grid>
          )}

        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button variant="contained" disabled={!pick?.video_url} onClick={handleConfirm} sx={{ fontWeight: 800 }}>
          {lang?.socialLibraryUseVideo || "Use this video"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
